/**
 * CelebrateSubmit.steps.jsx — the 7 wizard step panels extracted from
 * `CelebrateSubmit.jsx`. Each component takes a single `ctx` prop that
 * bundles every piece of state, setter and callback it needs. This keeps
 * the parent thin (state + orchestration + submit) while still allowing
 * all setters to live in one place.
 *
 * Props contract (`ctx`):
 *   form, set, setPrivacy                          — memorial form state
 *   addPortrait, removePortrait, onCover           — photo handlers
 *   aiSuggestions, aiBusy, requestAiBio            — step-4 AI helpers
 *   spouses, setSpouses, familyDetails,
 *   setFamilyDetails, relationships, setRelationships
 *   suggestions, suggesting, runSuggest,
 *   genealogyLinks, setGenealogyLinks              — step-5 family
 *   bioDetails, setBioDetails, legacy, setLegacy,
 *   audioTracks, setAudioTracks, places, setPlaces,
 *   timeline, setTimeline, services, setServices,
 *   funeralProgram, setFuneralProgram,
 *   charities, setCharities, cfg                   — step-6 extras
 *   submitter, setSubmitter, goTo,
 *   TYPE_OPTIONS                                    — step-7 review
 */
import {
  Bell, HeartHandshake, Upload, X, Plus, Trash2, Star, Search,
  TreePine, ExternalLink, Sparkles, Loader2,
} from "lucide-react";
import LocationCascade from "@/components/LocationCascade";
import FamilyDetailsPanel from "@/components/FamilyDetailsPanel";
import {
  OptionalAccordion, ReviewCard, Field, TextArea, AiServiceOrderSuggestor,
} from "./CelebrateSubmit.parts";

const border = { borderColor: "var(--wofy-border)" };
const emptyRel   = () => ({ relation: "child", name: "" });
const emptyTle   = () => ({ year: "", title: "", description: "", location: "" });
const emptySpouse = () => ({ name: "", is_former: false });

const TYPE_OPTIONS = [
  { key: "death_announcement", label: "Death Announcement",
    hint: "A formal notice with funeral / service details. Ideal within days of passing.",
    Icon: Bell },
  { key: "cherished_memorial", label: "Cherished Memorial",
    hint: "A lasting digital memorial with biography, timeline, photos & stories.",
    Icon: HeartHandshake },
];
export { TYPE_OPTIONS };

// ============ STEP 1 — Who ============
export function Step1Who({ form, set }) {
  return (
    <div className="space-y-4" data-testid="wizard-step-1-panel">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider mb-2">What are you creating?</div>
        <div className="grid md:grid-cols-2 gap-3">
          {TYPE_OPTIONS.map(({ key, label, hint, Icon }) => {
            const active = form.memorial_type === key;
            return (
              <button key={key} type="button" onClick={() => set("memorial_type", key)}
                data-testid={`mem-type-${key}`}
                className={`text-left rounded-xl border p-4 transition ${active ? "border-black bg-black text-white" : "hover:bg-black/5"}`}
                style={!active ? border : undefined}>
                <div className="flex items-center gap-2 font-medium"><Icon className="w-4 h-4" /> {label}</div>
                <div className={`text-[11px] mt-1 ${active ? "opacity-80" : "opacity-70"}`}>{hint}</div>
              </button>
            );
          })}
        </div>
      </div>
      <Field label="Full name *" required value={form.full_name} onChange={(v) => set("full_name", v)} testid="mem-name" placeholder="e.g. Mariamma Thomas Kizhakkethalackal" />
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Preferred / known-as name" value={form.preferred_name} onChange={(v) => set("preferred_name", v)} testid="mem-preferred" placeholder="What most people called them" />
        <Field label="Title" value={form.title} onChange={(v) => set("title", v)} testid="mem-title" placeholder="Dr., Rev., Col." />
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer opacity-70 hover:opacity-100">+ Add maiden / family / other names</summary>
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          <Field label="Maiden name" value={form.maiden_name} onChange={(v) => set("maiden_name", v)} testid="mem-maiden" />
          <Field label="Family name" value={form.family_name} onChange={(v) => set("family_name", v)} testid="mem-family-name" />
          <Field label="Nicknames (comma separated)" value={form.nicknames} onChange={(v) => set("nicknames", v)} />
          <Field label="Other known names" value={form.other_names} onChange={(v) => set("other_names", v)} />
        </div>
      </details>
    </div>
  );
}

// ============ STEP 2 — Life Overview ============
export function Step2Life({ form, set }) {
  return (
    <div className="space-y-4" data-testid="wizard-step-2-panel">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider mb-1">Where are they from?</div>
        <LocationCascade value={form.location} onChange={(loc) => set("location", loc)} testidPrefix="mem-loc" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field type="date" label="Date of birth" value={form.birth_date} onChange={(v) => set("birth_date", v)} testid="mem-birth-date" />
        <Field label="Birth place" value={form.birth_place} onChange={(v) => set("birth_place", v)} testid="mem-birth-place" placeholder="City / village" />
        <Field type="date" label="Date of passing" value={form.death_date} onChange={(v) => set("death_date", v)} testid="mem-death-date" />
        <Field label="Place of passing" value={form.death_place} onChange={(v) => set("death_place", v)} testid="mem-death-place" />
        {!form.birth_date && (
          <Field type="number" label="Age at passing *" placeholder="e.g. 86" value={form.age_at_death} onChange={(v) => set("age_at_death", v)} testid="mem-age" />
        )}
        <Field label="Gender" value={form.gender} onChange={(v) => set("gender", v)} placeholder="Female / Male / Other" />
      </div>
      <p className="text-[11px] italic" style={{ color: "var(--wofy-text-soft)" }}>
        Just the basics for now — you can add cause of death, religion, nationality, occupation and more on the Extras step.
      </p>
    </div>
  );
}

// ============ STEP 3 — Photo ============
export function Step3Photo({ form, set, addPortrait, removePortrait, onCover }) {
  return (
    <div className="space-y-4" data-testid="wizard-step-3-panel">
      <div className="grid md:grid-cols-5 gap-3">
        {form.portraits.map((src, i) => (
          <div key={i} className="relative">
            <img src={src} alt="" className="w-full h-32 object-cover rounded-lg border" style={border} />
            <button type="button" onClick={() => set("primary_portrait_index", i)}
              className={`absolute top-1 left-1 rounded-full p-1 shadow ${form.primary_portrait_index === i ? "bg-amber-400 text-black" : "bg-white/80 text-black/60"}`}
              data-testid={`mem-portrait-${i}-primary`} aria-label="Set as primary">
              <Star className={`w-3 h-3 ${form.primary_portrait_index === i ? "fill-current" : ""}`} />
            </button>
            <button type="button" onClick={() => removePortrait(i)}
              className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow" aria-label="Remove">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {form.portraits.length < 5 && (
          <label className={`border-2 border-dashed rounded-lg cursor-pointer flex flex-col items-center justify-center text-center ${form.portraits.length === 0 ? "col-span-full md:col-span-5 h-48" : "h-32"}`} style={border} data-testid="mem-portraits-add">
            <Upload className="w-6 h-6" />
            <div className="text-sm mt-2 font-medium">{form.portraits.length === 0 ? "Upload their photo" : "Add another"}</div>
            <div className="text-[11px] mt-1 max-w-xs" style={{ color: "var(--wofy-text-soft)" }}>
              {form.portraits.length === 0
                ? "A photo helps family members recognize and celebrate this person."
                : "Up to 5 photos. Click the ⭐ to set the primary."}
            </div>
            <input type="file" accept="image/*" multiple className="hidden" onChange={addPortrait} />
          </label>
        )}
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer opacity-70 hover:opacity-100">+ Add a cover photo & memorial quote (optional)</summary>
        <div className="mt-3 grid md:grid-cols-2 gap-3 items-start">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider mb-1">Cover photo</div>
            <div className="flex items-start gap-3">
              <label className="wofy-btn-ghost cursor-pointer" data-testid="mem-cover-btn">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={onCover} />
              </label>
              {form.cover_image && (
                <div className="relative">
                  <img src={form.cover_image} alt="" className="h-20 w-32 object-cover rounded-lg" />
                  <button type="button" onClick={() => set("cover_image", "")} className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow"><X className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          </div>
          <Field label='Memorial quote' placeholder='e.g. "A life well lived."' value={form.quote} onChange={(v) => set("quote", v)} />
        </div>
      </details>
    </div>
  );
}

// ============ STEP 4 — Story ============
export function Step4Story({ form, set, aiSuggestions, aiBusy, requestAiBio }) {
  return (
    <div className="space-y-4" data-testid="wizard-step-4-panel">
      <div className="rounded-lg border p-4" style={{ borderColor: "var(--wofy-primary)", background: "rgba(139,92,246,0.06)" }} data-testid="ai-bio-panel">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Sparkles className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
            <span>Stuck for words? Let AI suggest a starting point.</span>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => requestAiBio("warm")}   disabled={aiBusy} className="wofy-btn-ghost text-xs disabled:opacity-50" data-testid="ai-bio-warm">
              {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Warm
            </button>
            <button type="button" onClick={() => requestAiBio("formal")} disabled={aiBusy} className="wofy-btn-ghost text-xs disabled:opacity-50" data-testid="ai-bio-formal">Formal</button>
            <button type="button" onClick={() => requestAiBio("brief")}  disabled={aiBusy} className="wofy-btn-ghost text-xs disabled:opacity-50" data-testid="ai-bio-brief">Brief</button>
          </div>
        </div>
        <p className="text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
          AI will never invent facts — it only weaves together what you've entered so far. Always edit before publishing.
        </p>
        {aiSuggestions.length > 0 && (
          <div className="mt-3 space-y-2">
            {aiSuggestions.map((snippet, i) => (
              <div key={i} className="rounded-md bg-white border p-3 text-sm" style={{ borderColor: "var(--wofy-border)" }}>
                <p className="whitespace-pre-line">{snippet}</p>
                <div className="mt-2 flex gap-2 justify-end">
                  <button type="button" onClick={() => set("biography", (form.biography ? form.biography + "\n\n" : "") + snippet)} className="wofy-btn-ghost text-xs" data-testid={`ai-bio-append-${i}`}>Append to biography</button>
                  <button type="button" onClick={() => set("biography", snippet)} className="wofy-btn-primary text-xs" data-testid={`ai-bio-replace-${i}`}>Use this</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <TextArea label="Short intro / caption (1–2 sentences)" value={form.short_bio} onChange={(v) => set("short_bio", v)} rows={2} testid="mem-short-bio" />
      <TextArea label="Their life story" value={form.biography} onChange={(v) => set("biography", v)} rows={8} testid="mem-biography" />
      <p className="text-[11px] italic" style={{ color: "var(--wofy-text-soft)" }}>
        Tell us about their life, personality, accomplishments, favourite sayings, traditions, or special memories. Anything — future generations will thank you.
      </p>
      <details className="text-sm">
        <summary className="cursor-pointer opacity-70 hover:opacity-100">+ Add a formal obituary (optional)</summary>
        <div className="mt-3">
          <TextArea label="Obituary" value={form.obituary} onChange={(v) => set("obituary", v)} rows={5} testid="mem-obituary" />
        </div>
      </details>
    </div>
  );
}

// ============ STEP 5 — Family & genealogy ============
const GENEALOGY_SITES = [
  { k: "ancestry",         label: "Ancestry.com",   ph: "https://www.ancestry.com/family-tree/person/tree/…" },
  { k: "myheritage",       label: "MyHeritage",     ph: "https://www.myheritage.com/site-…" },
  { k: "familysearch",     label: "FamilySearch",   ph: "https://www.familysearch.org/tree/person/details/…" },
  { k: "twentythreeandme", label: "23andMe",        ph: "https://you.23andme.com/family/…" },
  { k: "findagrave",       label: "Find a Grave",   ph: "https://www.findagrave.com/memorial/…" },
  { k: "wikitree",         label: "WikiTree",       ph: "https://www.wikitree.com/wiki/…" },
  { k: "billiongraves",    label: "BillionGraves",  ph: "https://billiongraves.com/grave/…" },
  { k: "geni",             label: "Geni",           ph: "https://www.geni.com/people/…" },
  { k: "other",            label: "Other (any URL)", ph: "https://…" },
];

export function Step5Family(ctx) {
  const {
    form, set, spouses, setSpouses, familyDetails, setFamilyDetails,
    runSuggest, suggesting, suggestions,
    genealogyLinks, setGenealogyLinks, relationships, setRelationships, cfg,
  } = ctx;
  return (
    <div className="space-y-6" data-testid="wizard-step-5-panel">
      <div>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input type="checkbox" checked={form.is_married} onChange={(e) => set("is_married", e.target.checked)} data-testid="mem-is-married" />
          <span>Were they married or had a life partner?</span>
        </label>
        {form.is_married && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-mono uppercase tracking-wider">Spouse / partner</div>
              <button type="button" onClick={() => setSpouses([...spouses, emptySpouse()])} className="wofy-btn-ghost text-xs" data-testid="mem-add-spouse">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {spouses.map((s, i) => (
              <div key={i} className="grid md:grid-cols-4 gap-2 items-center mb-2">
                <input placeholder="Name" value={s.name} onChange={(e) => { const c = [...spouses]; c[i] = { ...s, name: e.target.value }; setSpouses(c); }} className="md:col-span-2 rounded border px-2 py-2 text-sm bg-transparent" style={border} />
                <label className="inline-flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={s.is_former} onChange={(e) => { const c = [...spouses]; c[i] = { ...s, is_former: e.target.checked }; setSpouses(c); }} />
                  Former
                </label>
                <button type="button" onClick={() => setSpouses(spouses.filter((_, j) => j !== i))} className="text-red-500 justify-self-start"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
        <FamilyDetailsPanel value={familyDetails} onChange={setFamilyDetails} isMarried={form.is_married} />
      </div>

      <div className="pt-4 border-t" style={border}>
        <div className="text-[11px] font-mono uppercase tracking-wider mb-2 flex items-center gap-2">
          <TreePine className="w-4 h-4" /> Link to a family tree
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button type="button" onClick={runSuggest} disabled={suggesting} className="wofy-btn-ghost text-xs" data-testid="mem-find-tree">
            <Search className="w-3 h-3" /> {suggesting ? "Searching…" : "Find matches"}
          </button>
          {form.linked_sp_id && (
            <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-emerald-100 text-emerald-800">
              Linked: {form.linked_sp_id}
            </span>
          )}
        </div>
        {suggestions && (
          <div className="space-y-3 mb-3">
            {(suggestions.submitter_trees || []).length > 0 && (
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Your family trees</div>
                <div className="grid md:grid-cols-2 gap-2">
                  {suggestions.submitter_trees.map((t) => (
                    <button key={t.id} type="button" onClick={() => set("family_tree_slug", t.slug)}
                      className={`text-left rounded-lg border px-3 py-2 text-sm hover:bg-black/5 ${form.family_tree_slug === t.slug ? "border-black" : ""}`}
                      style={border} data-testid={`mem-tree-your-${t.slug}`}>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-[11px] opacity-70">{t.role} · {t.country || "—"}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(suggestions.matched_persons || []).length > 0 && (
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Possible matches</div>
                <div className="space-y-1">
                  {suggestions.matched_persons.map((p) => (
                    <button key={p.sp_id} type="button" onClick={() => set("linked_sp_id", p.sp_id)}
                      className={`w-full text-left rounded-lg border px-3 py-2 text-sm hover:bg-black/5 ${form.linked_sp_id === p.sp_id ? "border-black" : ""}`}
                      style={border} data-testid={`mem-person-${p.sp_id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{p.full_name}</span>
                        <span className="text-[10px] font-mono opacity-70">{p.sp_id}</span>
                      </div>
                      <div className="text-[11px] opacity-70">{[p.birth_date?.slice(0, 4), p.death_date?.slice(0, 4)].filter(Boolean).join(" – ")} · {p.birth_place || ""}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(suggestions.matched_trees || []).length > 0 && (
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Family trees hosting a match</div>
                <div className="grid md:grid-cols-2 gap-2">
                  {suggestions.matched_trees.map((t) => (
                    <button key={t.id} type="button" onClick={() => set("family_tree_slug", t.slug)}
                      className={`text-left rounded-lg border px-3 py-2 text-sm hover:bg-black/5 ${form.family_tree_slug === t.slug ? "border-black" : ""}`}
                      style={border} data-testid={`mem-tree-match-${t.slug}`}>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-[11px] opacity-70">{t.country || "—"}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <Field label="Or paste a family tree slug" placeholder="mangalath-family" value={form.family_tree_slug} onChange={(v) => set("family_tree_slug", v)} testid="mem-tree-slug-manual" />
      </div>

      <div className="pt-4 border-t" style={border} data-testid="genealogy-links-section">
        <div className="text-[11px] font-mono uppercase tracking-wider mb-1 flex items-center gap-2">
          <ExternalLink className="w-4 h-4" /> Link to other genealogy sites
        </div>
        <p className="text-[11px] mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          Already have their profile on Ancestry, 23andMe, MyHeritage, FamilySearch or another site? Paste the URL — Social Pulse will cross-link them.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          {GENEALOGY_SITES.map(({ k, label, ph }) => (
            <label key={k} className="block">
              <span className="text-[11px] font-mono uppercase tracking-wider">{label}</span>
              <input type="url" value={genealogyLinks[k]}
                onChange={(e) => setGenealogyLinks((g) => ({ ...g, [k]: e.target.value }))}
                placeholder={ph}
                className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent"
                style={border} data-testid={`genealogy-link-${k}`} />
            </label>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t" style={border}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-mono uppercase tracking-wider">Other relationships (optional)</div>
          <button type="button" onClick={() => setRelationships([...relationships, emptyRel()])} className="wofy-btn-ghost text-xs">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        <div className="space-y-2">
          {relationships.map((r, i) => (
            <div key={i} className="grid md:grid-cols-3 gap-2 items-center">
              <select value={r.relation} onChange={(e) => { const c = [...relationships]; c[i] = { ...r, relation: e.target.value }; setRelationships(c); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border}>
                {cfg?.relation_types?.filter((rt) => !rt.includes("spouse")).map((rt) => <option key={rt} value={rt}>{rt.replace(/_/g, " ")}</option>)}
              </select>
              <input placeholder="Name" value={r.name} onChange={(e) => { const c = [...relationships]; c[i] = { ...r, name: e.target.value }; setRelationships(c); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
              <button type="button" onClick={() => setRelationships(relationships.filter((_, j) => j !== i))} className="text-red-500 justify-self-start"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ STEP 6 — Optional Details ============
export function Step6Extras(ctx) {
  const {
    form, set, cfg, bioDetails, setBioDetails, audioTracks, setAudioTracks,
    legacy, setLegacy, places, setPlaces, timeline, setTimeline,
    services, setServices, funeralProgram, setFuneralProgram,
    charities, setCharities,
  } = ctx;
  return (
    <div className="space-y-3" data-testid="wizard-step-6-panel">
      <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        Every field here is optional. Tap a section to expand it — skip anything that doesn't apply.
      </p>

      <OptionalAccordion title="Extended identity" testid="acc-identity">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Cause of death" value={form.cause_of_death} onChange={(v) => set("cause_of_death", v)} />
          <Field label="Nationality" value={form.nationality} onChange={(v) => set("nationality", v)} />
          <Field label="Citizenship" value={form.citizenship} onChange={(v) => set("citizenship", v)} />
          <Field label="Occupation" value={form.occupation} onChange={(v) => set("occupation", v)} />
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider">Profession</label>
            <select value={form.profession} onChange={(e) => set("profession", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={border}>
              <option value="">Select…</option>
              {cfg?.professions?.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <Field label="Education" value={form.education} onChange={(v) => set("education", v)} />
          <Field label="Military service" value={form.military_service} onChange={(v) => set("military_service", v)} />
          <Field label="Religion" value={form.religion} onChange={(v) => set("religion", v)} />
          <Field label="Languages (comma separated)" value={form.languages} onChange={(v) => set("languages", v)} />
        </div>
      </OptionalAccordion>

      <OptionalAccordion title="Life & legacy — hobbies, awards, quotes" testid="acc-legacy-ext">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Hobbies" value={bioDetails.hobbies} onChange={(v) => setBioDetails({ ...bioDetails, hobbies: v })} testid="mem-bio-hobbies" />
          <Field label="Talents" value={bioDetails.talents} onChange={(v) => setBioDetails({ ...bioDetails, talents: v })} />
          <Field label="Awards & recognition" value={bioDetails.awards} onChange={(v) => setBioDetails({ ...bioDetails, awards: v })} />
          <Field label="Organizations" value={bioDetails.organizations} onChange={(v) => setBioDetails({ ...bioDetails, organizations: v })} />
          <Field label="Clubs" value={bioDetails.clubs} onChange={(v) => setBioDetails({ ...bioDetails, clubs: v })} />
          <Field label="Favourite quote" value={bioDetails.favorite_quote} onChange={(v) => setBioDetails({ ...bioDetails, favorite_quote: v })} />
          <Field label="Words they lived by" value={bioDetails.words_lived_by} onChange={(v) => setBioDetails({ ...bioDetails, words_lived_by: v })} />
          <Field label="Community service" value={bioDetails.community_service} onChange={(v) => setBioDetails({ ...bioDetails, community_service: v })} />
          <Field label="Volunteer activities" value={bioDetails.volunteer} onChange={(v) => setBioDetails({ ...bioDetails, volunteer: v })} />
        </div>
      </OptionalAccordion>

      <OptionalAccordion title="Audio — favourite songs, voice messages, speeches" testid="acc-audio">
        <div className="flex items-center justify-end mb-2">
          <button type="button" onClick={() => setAudioTracks([...audioTracks, { title: "", url: "", kind: "favorite_song" }])} className="wofy-btn-ghost text-xs" data-testid="mem-audio-add">
            <Plus className="w-3 h-3" /> Add track
          </button>
        </div>
        {audioTracks.map((a, i) => (
          <div key={i} className="grid md:grid-cols-4 gap-2 items-center mb-2">
            <input placeholder="Title" value={a.title} onChange={(e) => { const c = [...audioTracks]; c[i] = { ...a, title: e.target.value }; setAudioTracks(c); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <input placeholder="URL" value={a.url} onChange={(e) => { const c = [...audioTracks]; c[i] = { ...a, url: e.target.value }; setAudioTracks(c); }} className="md:col-span-2 rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <div className="flex items-center gap-1">
              <select value={a.kind} onChange={(e) => { const c = [...audioTracks]; c[i] = { ...a, kind: e.target.value }; setAudioTracks(c); }} className="rounded border px-2 py-2 text-sm bg-transparent flex-1" style={border}>
                <option value="favorite_song">Favourite song</option>
                <option value="voice_message">Voice message</option>
                <option value="speech">Speech</option>
              </select>
              <button type="button" onClick={() => setAudioTracks(audioTracks.filter((_, j) => j !== i))} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </OptionalAccordion>

      <OptionalAccordion title="Legacy library — recipes, letters, journals, artwork" testid="acc-legacy">
        <div className="grid md:grid-cols-2 gap-3">
          {[
            ["recipes", "Favourite recipes"], ["letters", "Letters"], ["journals", "Journals"],
            ["artwork", "Artwork"], ["oral_histories", "Oral histories"], ["speeches", "Speeches"],
          ].map(([k, label]) => (
            <Field key={k} label={label} value={legacy[k]} onChange={(v) => setLegacy({ ...legacy, [k]: v })} testid={`mem-legacy-${k}`} />
          ))}
        </div>
      </OptionalAccordion>

      <OptionalAccordion title="Places of significance" testid="acc-places">
        <div className="flex items-center justify-end mb-2">
          <button type="button" onClick={() => setPlaces([...places, { kind: "hometown", name: "", location_slug: "", note: "" }])} className="wofy-btn-ghost text-xs" data-testid="mem-place-add">
            <Plus className="w-3 h-3" /> Add place
          </button>
        </div>
        {places.map((p, i) => (
          <div key={i} className="grid md:grid-cols-5 gap-2 items-center mb-2">
            <select value={p.kind} onChange={(e) => { const c = [...places]; c[i] = { ...p, kind: e.target.value }; setPlaces(c); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border}>
              <option value="hometown">Hometown</option>
              <option value="school">School</option>
              <option value="workplace">Workplace</option>
              <option value="church">Church / Temple / Mosque</option>
              <option value="military_unit">Military unit</option>
              <option value="cemetery">Cemetery</option>
            </select>
            <input placeholder="Name" value={p.name} onChange={(e) => { const c = [...places]; c[i] = { ...p, name: e.target.value }; setPlaces(c); }} className="rounded border px-2 py-2 text-sm bg-transparent md:col-span-2" style={border} />
            <input placeholder="Location slug (optional)" value={p.location_slug} onChange={(e) => { const c = [...places]; c[i] = { ...p, location_slug: e.target.value }; setPlaces(c); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <button type="button" onClick={() => setPlaces(places.filter((_, j) => j !== i))} className="text-red-500 justify-self-start"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </OptionalAccordion>

      <OptionalAccordion title="Life timeline (birth, marriage, milestones…)" testid="acc-timeline">
        <div className="flex items-center justify-end mb-2">
          <button type="button" onClick={() => setTimeline([...timeline, emptyTle()])} className="wofy-btn-ghost text-xs"><Plus className="w-3 h-3" /> Add event</button>
        </div>
        <div className="space-y-3">
          {timeline.map((t, i) => (
            <div key={i} className="grid md:grid-cols-5 gap-2 items-start">
              <input type="number" placeholder="Year" value={t.year} onChange={(e) => { const c = [...timeline]; c[i] = { ...t, year: e.target.value }; setTimeline(c); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
              <input placeholder="Title" value={t.title} onChange={(e) => { const c = [...timeline]; c[i] = { ...t, title: e.target.value }; setTimeline(c); }} className="md:col-span-2 rounded border px-2 py-2 text-sm bg-transparent" style={border} />
              <input placeholder="Location" value={t.location} onChange={(e) => { const c = [...timeline]; c[i] = { ...t, location: e.target.value }; setTimeline(c); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
              <button type="button" onClick={() => setTimeline(timeline.filter((_, j) => j !== i))} className="text-red-500 self-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </OptionalAccordion>

      <OptionalAccordion title="Funeral / memorial service" testid="acc-funeral">
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Funeral home" value={form.funeral_home} onChange={(v) => set("funeral_home", v)} />
          <Field type="date" label="Date" value={form.funeral_date} onChange={(v) => set("funeral_date", v)} />
          <Field type="time" label="Time" value={form.funeral_time} onChange={(v) => set("funeral_time", v)} />
          <Field label="Location" value={form.funeral_location} onChange={(v) => set("funeral_location", v)} />
          <Field label="Livestream URL" value={form.funeral_livestream} onChange={(v) => set("funeral_livestream", v)} />
          <Field label="Dress code" value={form.funeral_dress} onChange={(v) => set("funeral_dress", v)} />
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider">Flowers accepted?</label>
            <select value={form.flowers_accepted} onChange={(e) => set("flowers_accepted", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={border}>
              <option value="yes">Yes</option><option value="no">No</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider">Charity donations preferred?</label>
            <select value={form.donations_preferred} onChange={(e) => set("donations_preferred", e.target.value)} className="w-full mt-1 rounded-lg border px-3 py-2 text-sm bg-transparent" style={border}>
              <option value="no">No</option><option value="yes">Yes</option>
            </select>
          </div>
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider mt-4 mb-1">Cemetery</div>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Cemetery" value={form.cemetery_name} onChange={(v) => set("cemetery_name", v)} />
          <Field label="Section" value={form.cemetery_section} onChange={(v) => set("cemetery_section", v)} />
          <Field label="Plot" value={form.cemetery_plot} onChange={(v) => set("cemetery_plot", v)} />
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider mt-4 mb-1">Wake, viewing & other services</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Viewing / visitation" value={services.viewing} onChange={(v) => setServices({ ...services, viewing: v })} />
          <Field label="Wake details" value={services.wake} onChange={(v) => setServices({ ...services, wake: v })} />
          <Field label="Memorial service" value={services.memorial_service} onChange={(v) => setServices({ ...services, memorial_service: v })} />
          <Field label="Celebration of life" value={services.celebration_of_life} onChange={(v) => setServices({ ...services, celebration_of_life: v })} />
          <Field type="date" label="Burial date" value={services.burial_date} onChange={(v) => setServices({ ...services, burial_date: v })} />
          <Field type="time" label="Burial time" value={services.burial_time} onChange={(v) => setServices({ ...services, burial_time: v })} />
          <Field type="date" label="Cremation date" value={services.cremation_date} onChange={(v) => setServices({ ...services, cremation_date: v })} />
          <Field label="Crematorium" value={services.crematorium} onChange={(v) => setServices({ ...services, crematorium: v })} />
          <Field label="Ashes resting place" value={services.ashes_resting_place} onChange={(v) => setServices({ ...services, ashes_resting_place: v })} />
          <Field label="Live stream URL" value={services.live_stream_url} onChange={(v) => setServices({ ...services, live_stream_url: v })} />
          <Field label="Online meeting link" value={services.online_meeting_url} onChange={(v) => setServices({ ...services, online_meeting_url: v })} />
          <Field label="Funeral director" value={services.funeral_director} onChange={(v) => setServices({ ...services, funeral_director: v })} />
        </div>
      </OptionalAccordion>

      <OptionalAccordion title="Funeral program (order of service)" testid="acc-program">
        <AiServiceOrderSuggestor
          form={form}
          onApply={(res) => {
            const joinList = (arr, fmt) => (arr || []).map(fmt).join("\n");
            setFuneralProgram((fp) => ({
              ...fp,
              order_of_service: joinList(res.order_of_service, (s) => `${s.step}. ${s.title}${s.description ? ` — ${s.description}` : ""}`),
              hymns:            joinList(res.hymns,    (h) => `${h.title}${h.note ? ` (${h.note})` : ""}`),
              readings:         joinList(res.readings, (r) => `${r.title}${r.note ? ` (${r.note})` : ""}`),
              speakers:         joinList(res.speakers, (s) => `${s.role}${s.note ? ` — ${s.note}` : ""}`),
              pallbearers:      joinList(res.pallbearers, (p) => `${p.role}${p.note ? ` — ${p.note}` : ""}`),
              honorary_pallbearers: joinList(res.honorary_pallbearers, (p) => `${p.role}${p.note ? ` — ${p.note}` : ""}`),
              reception:        res.reception || fp.reception,
            }));
          }}
        />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Program PDF URL" value={funeralProgram.pdf_url} onChange={(v) => setFuneralProgram({ ...funeralProgram, pdf_url: v })} testid="mem-program-pdf" />
          <Field label="Order of service" value={funeralProgram.order_of_service} onChange={(v) => setFuneralProgram({ ...funeralProgram, order_of_service: v })} />
          <Field label="Hymns / songs" value={funeralProgram.hymns} onChange={(v) => setFuneralProgram({ ...funeralProgram, hymns: v })} />
          <Field label="Readings / poems" value={funeralProgram.readings} onChange={(v) => setFuneralProgram({ ...funeralProgram, readings: v })} />
          <Field label="Speakers" value={funeralProgram.speakers} onChange={(v) => setFuneralProgram({ ...funeralProgram, speakers: v })} />
          <Field label="Pallbearers" value={funeralProgram.pallbearers} onChange={(v) => setFuneralProgram({ ...funeralProgram, pallbearers: v })} />
          <Field label="Honorary pallbearers" value={funeralProgram.honorary_pallbearers} onChange={(v) => setFuneralProgram({ ...funeralProgram, honorary_pallbearers: v })} />
          <Field label="Reception" value={funeralProgram.reception} onChange={(v) => setFuneralProgram({ ...funeralProgram, reception: v })} />
          <Field label="Parking" value={funeralProgram.parking} onChange={(v) => setFuneralProgram({ ...funeralProgram, parking: v })} />
          <Field label="Accommodation" value={funeralProgram.accommodation} onChange={(v) => setFuneralProgram({ ...funeralProgram, accommodation: v })} />
        </div>
      </OptionalAccordion>

      <OptionalAccordion title="Charity legacy" testid="acc-charity">
        <div className="flex items-center justify-end mb-2">
          <button type="button" onClick={() => setCharities([...charities, { name: "", url: "", note: "" }])} className="wofy-btn-ghost text-xs"><Plus className="w-3 h-3" /> Add charity</button>
        </div>
        {charities.map((c, i) => (
          <div key={i} className="grid md:grid-cols-4 gap-2 items-center mb-2">
            <input placeholder="Charity name" value={c.name} onChange={(e) => { const cs = [...charities]; cs[i] = { ...c, name: e.target.value }; setCharities(cs); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <input placeholder="URL" value={c.url} onChange={(e) => { const cs = [...charities]; cs[i] = { ...c, url: e.target.value }; setCharities(cs); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <input placeholder="Why this cause was important" value={c.note} onChange={(e) => { const cs = [...charities]; cs[i] = { ...c, note: e.target.value }; setCharities(cs); }} className="rounded border px-2 py-2 text-sm bg-transparent" style={border} />
            <button type="button" onClick={() => setCharities(charities.filter((_, j) => j !== i))} className="text-red-500 justify-self-start"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </OptionalAccordion>

      <OptionalAccordion title="Privacy preferences" testid="acc-privacy">
        <div className="text-[11px] font-mono uppercase tracking-wider mb-2">Default privacy</div>
        <div className="flex flex-wrap gap-3 text-sm">
          {["public", "family", "private"].map((p) => (
            <label key={p} className="inline-flex items-center gap-2">
              <input type="radio" name="privacy" value={p} checked={form.privacy === p} onChange={() => set("privacy", p)} />
              <span className="capitalize">{p}</span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
          Name, location and basic identity are always public per platform rules.
        </p>
      </OptionalAccordion>
    </div>
  );
}

// ============ STEP 7 — Review & Publish ============
export function Step7Review(ctx) {
  const {
    form, goTo, submitter, setSubmitter, set,
    genealogyLinks, spouses, relationships, timeline, places, audioTracks, charities,
    RECAPTCHA_SITE_KEY,
  } = ctx;
  return (
    <div className="space-y-4" data-testid="wizard-step-7-panel">
      <ReviewCard label="Who" goTo={() => goTo(1)} rows={[
        ["Name", form.full_name],
        ["Type", TYPE_OPTIONS.find((t) => t.key === form.memorial_type)?.label],
        ["Preferred name", form.preferred_name],
      ]} />
      <ReviewCard label="Life" goTo={() => goTo(2)} rows={[
        ["Country", form.location.country],
        ["Birth", [form.birth_date, form.birth_place].filter(Boolean).join(" · ")],
        ["Passing", [form.death_date || (form.age_at_death ? `age ${form.age_at_death}` : ""), form.death_place].filter(Boolean).join(" · ")],
      ]} />
      <ReviewCard label="Photo" goTo={() => goTo(3)} rows={[["Photos uploaded", form.portraits.length]]}
        preview={form.portraits[form.primary_portrait_index] || form.portraits[0]} />
      <ReviewCard label="Story" goTo={() => goTo(4)} rows={[
        ["Short intro", form.short_bio ? `${form.short_bio.slice(0, 80)}${form.short_bio.length > 80 ? "…" : ""}` : ""],
        ["Biography", form.biography ? `${form.biography.length} chars` : ""],
      ]} />
      <ReviewCard label="Family" goTo={() => goTo(5)} rows={[
        ["Family tree", form.family_tree_slug || (form.linked_sp_id ? `Linked person: ${form.linked_sp_id}` : "")],
        ["External profiles", Object.values(genealogyLinks).filter(Boolean).length + " link(s)"],
        ["Relationships", (spouses.filter((s) => s.name).length + relationships.filter((r) => r.name).length) + " entries"],
      ]} />
      <ReviewCard label="Extras" goTo={() => goTo(6)} rows={[
        ["Timeline events", timeline.filter((t) => t.title).length],
        ["Places", places.filter((p) => p.name).length],
        ["Audio tracks", audioTracks.filter((a) => a.title || a.url).length],
        ["Charities", charities.filter((c) => c.name).length],
      ]} />

      <div className="wofy-card">
        <div className="text-[11px] font-mono uppercase tracking-wider mb-2">YOUR DETAILS (AS SUBMITTER)</div>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Your name *" required value={submitter.name} onChange={(v) => setSubmitter({ ...submitter, name: v })} testid="mem-submitter-name" />
          <Field label="Email *" required type="email" value={submitter.email} onChange={(v) => setSubmitter({ ...submitter, email: v })} testid="mem-submitter-email" />
          <Field label="Phone" value={submitter.phone} onChange={(v) => setSubmitter({ ...submitter, phone: v })} />
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} data-testid="mem-consent" />
        <span>I confirm this information is accurate, I have the right to share it, and I understand the memorial will be reviewed by Social Pulse moderators before publication.</span>
      </label>

      {RECAPTCHA_SITE_KEY && (
        <p className="text-[10px] font-mono" style={{ color: "var(--wofy-text-soft)" }}>
          Protected by reCAPTCHA · <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a> · <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline">Terms</a>
        </p>
      )}
    </div>
  );
}
